import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { dataLibraryApi } from '../../services/api/dataLibrary';
import type { DataLibrarySurveyRow, DataLibraryCohortGroup } from '../../types';

// ============================================================================
// Types
// ============================================================================

export interface CoachDashboardFilters {
  coaches: string[];
  overallSatisfaction: string[];
  cohortIds: string[];
}

export interface DrilldownState {
  chartSection: string;
  segmentLabel: string;
  segmentValue: string;
  rows: DataLibrarySurveyRow[];
}

// ============================================================================
// Constants
// ============================================================================

export const SATISFACTION_ORDER = [
  'Very satisfied',
  'Satisfied',
  'Neutral',
  'Neither satisfied nor dissatisfied',
  'Dissatisfied',
  'Very dissatisfied',
];

export const COACH_SATISFACTION_ORDER = [
  'Strongly agree',
  'Agree',
  'Neutral',
  'Disagree',
  'Strongly disagree',
];

export const SATISFACTION_COLORS: Record<string, string> = {
  'Very satisfied': '#28463A',
  'Satisfied': '#4A7C59',
  'Neutral': '#E9A23B',
  'Neither satisfied nor dissatisfied': '#E9A23B',
  'Dissatisfied': '#E07B4C',
  'Very dissatisfied': '#D64545',
  // Coach satisfaction (agreement scale) uses the same tier colors
  'Strongly agree': '#28463A',
  'Agree': '#4A7C59',
  'Disagree': '#E07B4C',
  'Strongly disagree': '#D64545',
};

// ============================================================================
// Hook
// ============================================================================

export function useCoachDashboardData() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Data state
  const [allRows, setAllRows] = useState<DataLibrarySurveyRow[]>([]);
  const [cohortGroups, setCohortGroups] = useState<DataLibraryCohortGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingCohorts, setLoadingCohorts] = useState(true);

  // Parse filters from URL
  const filters: CoachDashboardFilters = useMemo(() => ({
    coaches: searchParams.get('coaches')?.split(',').filter(Boolean) || [],
    overallSatisfaction: searchParams.get('satisfaction')?.split(',').filter(Boolean) || [],
    cohortIds: searchParams.get('cohorts')?.split(',').filter(Boolean) || [],
  }), [searchParams]);

  // Update filters in URL
  const setFilters = useCallback((newFilters: Partial<CoachDashboardFilters>) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (newFilters.coaches !== undefined) {
        if (newFilters.coaches.length > 0) next.set('coaches', newFilters.coaches.join(','));
        else next.delete('coaches');
      }
      if (newFilters.overallSatisfaction !== undefined) {
        if (newFilters.overallSatisfaction.length > 0) next.set('satisfaction', newFilters.overallSatisfaction.join(','));
        else next.delete('satisfaction');
      }
      if (newFilters.cohortIds !== undefined) {
        if (newFilters.cohortIds.length > 0) next.set('cohorts', newFilters.cohortIds.join(','));
        else next.delete('cohorts');
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const resetFilters = useCallback(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  // Fetch cohorts
  const fetchCohorts = useCallback(async () => {
    try {
      setLoadingCohorts(true);
      const data = await dataLibraryApi.getCohorts();
      setCohortGroups(data);
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to load cohorts', color: 'red' });
    } finally {
      setLoadingCohorts(false);
    }
  }, []);

  // All cohort IDs (flattened from groups)
  const allCohortIds = useMemo(
    () => cohortGroups.flatMap(g => g.cohorts.map(c => c.id)),
    [cohortGroups],
  );

  // All cohort options for the filter dropdown
  const cohortOptions = useMemo(
    () => cohortGroups.flatMap(g => g.cohorts.map(c => ({ value: c.id, label: c.name }))),
    [cohortGroups],
  );

  // Fetch survey responses for ALL cohorts (we filter client-side)
  const fetchData = useCallback(async () => {
    if (allCohortIds.length === 0) return;

    try {
      setLoading(true);
      const result = await dataLibraryApi.getSurveyResponses({
        cohortIds: allCohortIds,
        formTypes: ['mid_program', 'final_survey'],
        limit: 10000,
        page: 1,
        excludePartnerCoaches: true,
      });
      setAllRows(result.data || []);
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to load survey responses', color: 'red' });
    } finally {
      setLoading(false);
    }
  }, [allCohortIds]);

  useEffect(() => {
    fetchCohorts();
  }, [fetchCohorts]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Apply client-side filters
  const filteredRows = useMemo(() => {
    let rows = allRows;

    if (filters.cohortIds.length > 0) {
      rows = rows.filter(r => filters.cohortIds.includes(r.cohort_id));
    }
    if (filters.coaches.length > 0) {
      rows = rows.filter(r => r.coach_email && filters.coaches.includes(r.coach_email));
    }
    if (filters.overallSatisfaction.length > 0) {
      rows = rows.filter(r => r.overall_satisfaction && filters.overallSatisfaction.includes(r.overall_satisfaction));
    }

    return rows;
  }, [allRows, filters]);

  // Unique coaches in the data (for filter dropdown)
  const coachOptions = useMemo(() => {
    const coachMap = new Map<string, string>();
    for (const row of allRows) {
      if (row.coach_email && !coachMap.has(row.coach_email)) {
        coachMap.set(row.coach_email, row.coach_name || row.coach_email);
      }
    }
    return Array.from(coachMap.entries())
      .map(([email, name]) => ({ value: email, label: name }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allRows]);

  // Unique satisfaction values in the data (for filter dropdown)
  const satisfactionOptions = useMemo(() => {
    const values = new Set<string>();
    for (const row of allRows) {
      if (row.overall_satisfaction) values.add(row.overall_satisfaction);
    }
    return SATISFACTION_ORDER.filter(v => values.has(v));
  }, [allRows]);

  const hasActiveFilters = filters.coaches.length > 0 || filters.overallSatisfaction.length > 0 || filters.cohortIds.length > 0;

  return {
    // Data
    filteredRows,
    allRows,
    loading: loading || loadingCohorts,

    // Filters
    filters,
    setFilters,
    resetFilters,
    hasActiveFilters,

    // Filter options
    coachOptions,
    satisfactionOptions,
    cohortOptions,
  };
}
